type CardVariant = "silver" | "black" | "metal";

type CreditCardMockProps = {
  variant: CardVariant;
  label: string;
  lastDigits: string;
  cardholder: string;
  expires: string;
  className?: string;
};

const variantStyles: Record<CardVariant, { background: string; textClass: string; labelClass: string }> = {
  silver: {
    background: "var(--gradient-card-silver)",
    textClass: "text-[#020099]",
    labelClass: "text-[#0400CC]/60",
  },
  black: {
    background: "var(--gradient-card)",
    textClass: "text-[#ffffff]",
    labelClass: "text-[#ccccff]",
  },
  metal: {
    background: "var(--gradient-card-metal)",
    textClass: "text-[#c8c8cc]",
    labelClass: "text-[#7a7a80]",
  },
};

export const CreditCardMock = ({
  variant,
  label,
  lastDigits,
  cardholder,
  expires,
  className = "",
}: CreditCardMockProps) => {
  const styles = variantStyles[variant];

  return (
    <div
      className={`card-shine relative aspect-[1.586/1] w-full overflow-hidden rounded-[1.5rem] border border-[#3a3a40]/60 shadow-[0_25px_60px_-12px_rgba(0,0,0,0.5)] ${className}`}
      style={{ background: styles.background, containerType: 'inline-size' }}
    >
      {/* Vertical ribbed stripes for metal variant */}
      {variant === "metal" && (
        <div
          className="absolute inset-0 z-[1] opacity-[0.18]"
          style={{
            backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 3px, rgba(255,255,255,0.15) 3px, rgba(255,255,255,0.15) 4px)',
          }}
        />
      )}
      <div className="relative z-10 flex h-full flex-col justify-between p-[7%]">
        {/* Top row: logo + label */}
        <div className="flex items-start justify-between">
          <img
            src="/trust-assets/wallets/trust-logo-name-white.png"
            alt="Trust logo"
            className="w-auto object-contain"
            style={{ height: '2.2rem' }}
          />
          <span className={`text-[0.6rem] font-medium uppercase tracking-[0.35em] ${styles.labelClass}`}>
            {label}
          </span>
        </div>

        {/* Bottom content */}
        <div className="flex flex-col gap-[5%]">
          <img
            src="/trust-assets/card-chip.png"
            alt="EMV chip"
            className="h-[13%] w-auto max-w-[15%] rounded-sm object-cover"
            loading="lazy"
          />

          <div className={`whitespace-nowrap font-mono tracking-[0.3em] ${styles.textClass}`} style={{ fontSize: 'clamp(0.65rem, 2.5cqi, 1.5rem)' }}>
            •••• •••• •••• {lastDigits}
          </div>

          <div className="flex items-end justify-between gap-2">
            <div className="min-w-0">
              <div className={`whitespace-nowrap uppercase tracking-[0.2em] ${styles.labelClass}`} style={{ fontSize: 'clamp(0.35rem, 1cqi, 0.55rem)' }}>
                Cardholder
              </div>
              <div className={`mt-0.5 whitespace-nowrap font-semibold uppercase tracking-[0.06em] ${styles.textClass}`} style={{ fontSize: 'clamp(0.5rem, 1.8cqi, 0.875rem)' }}>
                {cardholder}
              </div>
            </div>
            <div className="text-right">
              <div className={`whitespace-nowrap uppercase tracking-[0.2em] ${styles.labelClass}`} style={{ fontSize: 'clamp(0.35rem, 1cqi, 0.55rem)' }}>
                Expires
              </div>
              <div className={`mt-0.5 whitespace-nowrap font-semibold tracking-[0.06em] ${styles.textClass}`} style={{ fontSize: 'clamp(0.5rem, 1.8cqi, 0.875rem)' }}>
                {expires}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditCardMock;
